import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Markdown from 'react-markdown';

import styled from 'styled-components';

import {
    View,
    Button,
    Icon,
    Grid,
    GridColumn,
    css,
    colors,
} from '@fairygodboss/ui';

const removeEscapeChars = (src) => src.replace(/\\/g, '');

const MarkDownBody = styled(View)`
    color: #353535;
    font-size: 16px;
    font-weight: normal;
    line-height: 26px;
    letter-spacing: 0;
    p {
        line-height: 26px;
    }
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        font-family: 'Open Sans', sans-serif;
        font-weight: normal;
        &,
        strong {
            font-weight: normal;
        }
    }

    h1 {
        font-size: 20px;
        line-height: 28px;
    }
    h2 {
        font-size: 18px;
        line-height: 26px;
    }
    h3,
    h4,
    h5,
    h6 {
        font-size: 16px;
        line-height: 24px;
    }
`;

export const Body = ({ jobRequestId, description, fallbackDescription }) => {
    const [collapse, setCollapse] = useState(false);
    const [isCollapsable, setIsCollapsable] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (bodyRef?.current) {
            const height = bodyRef?.current.offsetHeight;
            if (height > 430) {
                setCollapse(true);
            }
        }
    }, []);

    useEffect(() => {
        if (bodyRef?.current) {
            const height = bodyRef?.current?.offsetHeight;
            if (height > 430 && !isCollapsable) {
                setIsCollapsable(true);
            }
        }
    }, [isCollapsable, bodyRef]);

    return (
        <View
            py="large"
            borderBottomStyle="solid"
            borderWidth="1px"
            borderColor={colors.secondary[20]}
        >
            <Grid>
                <GridColumn size={{ md: 10.5 }}>
                    <View
                        as="p"
                        color={colors.neutral[50]}
                        fontSize="11px"
                        lineHeight="26px"
                        mb="16px"
                    >
                        #{jobRequestId}
                    </View>
                    <View
                        as="h3"
                        fontFamily="Open Sans, sans-serif"
                        fontSize="16px"
                        fontWeight={700}
                        lineHeight="26px"
                        margin="0 0 24px 0"
                        color={colors.neutral[100]}
                    >
                        Position summary
                    </View>
                </GridColumn>
            </Grid>
            <Grid>
                <GridColumn size={{ md: 10.5 }}>
                    <View
                        position="relative"
                        ref={bodyRef}
                        {...(collapse && {
                            height: { _: '736px', md: '430px' },
                            overflow: 'hidden',
                        })}
                    >
                            {collapse && (
                            <View
                                bottom="0"
                                width="100%"
                                height={{ _: '140px', md: '170px' }}
                                position="absolute"
                                css={css({
                                    pointerEvents: 'none',
                                    backgroundImage:
                                        '-webkit-linear-gradient(top, rgba(255,255,255,0.001) 0%, #fff 100%)',
                                })}
                            />
                        )}
                        {description ? (
                            <MarkDownBody pb="24px" as="span">
                                <Markdown>
                                    {removeEscapeChars(description)}
                                </Markdown>
                            </MarkDownBody>
                        ) : (
                            <View
                                fontSize="16px"
                                lineHeight="26px"
                                pb="24px"
                                color={colors.neutral[100]}
                                as="span"
                            >
                                {fallbackDescription}
                            </View>
                        )}
                    </View>
                    {isCollapsable ? (
                        <View justifyContent="center" display="flex" pt="4px">
                            <Button
                                data-qa="show-more-button"
                                variant="primaryText"
                                size="medium"
                                onPress={() => setCollapse(!collapse)}
                                rightIcon={
                                    collapse ? (
                                        <Icon
                                            variant="chevron_down_16"
                                            outlineColor={colors.primary[60]}
                                        />
                                    ) : (
                                        <Icon
                                            variant="chevron_up_16"
                                            outlineColor={colors.primary[60]}
                                        />
                                    )
                                }
                            >
                                Show {collapse ? 'more' : 'less'}
                            </Button>
                        </View>
                    ) : undefined}
                </GridColumn>
            </Grid>
        </View>
    );
};

Body.propTypes = {
    jobRequestId: PropTypes.string,
    description: PropTypes.string,
    fallbackDescription: PropTypes.string,
    jobSearchVariant: PropTypes.bool,
};

Body.defaultProps = {
    jobSearchVariant: false,
    jobRequestId: null,
    description: null,
    fallbackDescription: null,
};
